import { useState } from 'react';

export function WaitlistPage() {
  const [email, setEmail] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [adSpend, setAdSpend] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, storeUrl, adSpend }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to join waitlist');
      }

      setSubmitted(true);
      setEmail('');
      setStoreUrl('');
      setAdSpend('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-off-white min-h-screen">
      {/* Hero Section */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-hero">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-olive-900 mb-6">
            Join the Sareto Waitlist
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Be first in line when we launch. Early members get priority access and <span className="font-semibold text-olive-700">$49/mo launch pricing locked in</span>.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Quick Value Recap */}
            <div>
              <h2 className="text-xl font-bold text-olive-900 mb-6">
                What you're signing up for:
              </h2>
              <ul className="space-y-4">
                {[
                  "Server-side Meta tracking that actually works",
                  "One-click Shopify installation (no code, no developers)",
                  "Essential metrics: revenue recovered, purchases recovered, pixel health",
                  "$49/month flat (no tiers, no limits)",
                  "Early access before public launch"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="text-olive-600 mt-0.5 flex-shrink-0 text-lg">✓</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              
              {/* What Happens Next */}
              <div className="mt-10">
                <h3 className="text-lg font-bold text-olive-900 mb-4">
                  What happens next
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-olive-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-olive-700 font-semibold text-sm">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-olive-900">You'll get a confirmation email</p>
                      <p className="text-sm text-gray-600">Just making sure you're human.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-olive-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-olive-700 font-semibold text-sm">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-olive-900">We'll notify you at launch</p>
                      <p className="text-sm text-gray-600">Early waitlist = first access.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 bg-olive-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-olive-700 font-semibold text-sm">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-olive-900">Your price locks in at $49/mo</p>
                      <p className="text-sm text-gray-600">Even if we raise it later.</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-6">
                  No spam. No 47-email nurture sequence. Just access when we're ready.
                </p>
              </div>
            </div>

            {/* Form */}
            <div className="glass-strong rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-olive-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-olive-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-olive-900 mb-2">You're on the list!</h3>
                  <p className="text-gray-600 mb-4">
                    Check your inbox for a confirmation email.
                  </p>
                  <p className="text-sm text-gray-500">
                    We'll notify you as soon as Sareto is ready for you.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-olive-900 mb-2">
                      Email Address <span className="text-olive-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(null);
                      }}
                      placeholder="you@yourstore.com"
                      required
                      className="input-glass w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="storeUrl" className="block text-sm font-medium text-olive-900 mb-2">
                      Store URL <span className="text-gray-500">(optional)</span>
                    </label>
                    <input
                      type="url"
                      id="storeUrl"
                      value={storeUrl}
                      onChange={(e) => setStoreUrl(e.target.value)}
                      placeholder="yourstore.myshopify.com"
                      className="input-glass w-full px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Helps us prioritize Shopify merchants
                    </p>
                  </div>
                  
                  <div>
                    <label htmlFor="adSpend" className="block text-sm font-medium text-olive-900 mb-2">
                      Monthly Ad Spend <span className="text-gray-500">(optional)</span>
                    </label>
                    <select
                      id="adSpend"
                      value={adSpend}
                      onChange={(e) => setAdSpend(e.target.value)}
                      className="input-glass w-full px-4 py-3 rounded-lg text-gray-900 bg-white"
                    >
                      <option value="">Select range...</option>
                      <option value="under-2k">Under $2K</option>
                      <option value="2k-5k">$2K - $5K</option>
                      <option value="5k-15k">$5K - $15K</option>
                      <option value="15k-50k">$15K - $50K</option>
                      <option value="50k-plus">$50K+</option>
                    </select>
                  </div>
                  
                  {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full px-6 py-4 rounded-lg font-semibold text-white disabled:opacity-50 transition-all"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Joining...
                      </span>
                    ) : (
                      'Join the Waitlist →'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Placeholder */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="glass-olive rounded-xl p-8 text-center">
            <p className="text-olive-700 font-medium">
              Join merchants already on the waitlist
            </p>
            {/* Future: Add count, logos, testimonials */}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-olive-900 text-olive-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-white mb-2">Sareto</h3>
              <p className="text-sm text-olive-300">Server-side tracking for Shopify. One click. $49/mo.</p>
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <a href="/#how-it-works" className="hover:text-white transition-colors">How It Works</a>
              <a href="/#pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="/#faq" className="hover:text-white transition-colors">FAQ</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
          </div>
          <div className="pt-8 border-t border-olive-800 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-olive-400">
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
            </div>
            <p>© 2025 Sareto</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
